import { CurrencyPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { FormSaverComponent, ToggleCardComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { catchError, map, Observable, of, startWith, tap } from 'rxjs';
import { StripeAdapter } from '../../adapters/stripe.adapter';
import { CreditsAutoRenewalLimit } from '../../enums/stripe.enum';
import { SubscriptionSummary } from '../../interfaces/subscription';
import * as SubscriptionActions from '../../store/subscription/subscription.actions';
import { selectSubscription } from '../../store/subscription/subscription.reducer';
import { NumberStepperComponent } from '../number-stepper/number-stepper.component';

export type CreditsAutoRenewalPackageOption = {
  id: string;
  credits: number;
  unitAmountCents: number;
  currency: string;
};

const MIN_CREDITS_AUTO_RENEWAL_PACKAGES = 1;
const DEFAULT_CREDITS_AUTO_RENEWAL_CREDITS = 1000;
const DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER = 10;
const DEFAULT_CREDITS_AUTO_RENEWAL_LIMIT = CreditsAutoRenewalLimit.UNLIMITED;

let nextCreditsAutoRenewalSetupId = 0;

@Component({
  selector: 'app-credits-auto-renewal-setup',
  templateUrl: './credits-auto-renewal-setup.component.html',
  styleUrl: './credits-auto-renewal-setup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CurrencyPipe,
    DecimalPipe,
    FormSaverComponent,
    NgSelectModule,
    NumberStepperComponent,
    ReactiveFormsModule,
    ToggleCardComponent,
    TranslocoPipe,
  ],
})
export class CreditsAutoRenewalSetupComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly stripeAdapter = inject(StripeAdapter);
  private readonly toastsService = inject(ToastsService);
  private readonly formSaver = viewChild<FormSaverComponent>('formSaver');

  /** Whether a stored card, SEPA mandate or invoice payment is available. */
  readonly canEnable = input(false);
  /** Shows the sticky save bar used on the payment settings page. */
  readonly showFormSaver = input(false);
  /** Starts with auto-renewal switched on (e.g. first-booking prompt). */
  readonly startEnabled = input(false);

  readonly minPackages = MIN_CREDITS_AUTO_RENEWAL_PACKAGES;
  readonly instanceId = `credits-auto-renewal-${++nextCreditsAutoRenewalSetupId}`;
  readonly translationBase =
    'company-tool.views.settings.company.payment.sections.credits.auto-renewal';
  readonly limits: ReadonlyArray<{
    value: CreditsAutoRenewalLimit;
    labelKey: string;
  }> = [
    {
      value: CreditsAutoRenewalLimit.UNLIMITED,
      labelKey: `${this.translationBase}.limit-unlimited`,
    },
    {
      value: CreditsAutoRenewalLimit.MONTH,
      labelKey: `${this.translationBase}.limit-month`,
    },
    {
      value: CreditsAutoRenewalLimit.YEAR,
      labelKey: `${this.translationBase}.limit-year`,
    },
  ];

  readonly form = this.fb.group({
    creditsAutoRenewalEnabled: this.fb.nonNullable.control(false),
    limit: this.fb.nonNullable.control(DEFAULT_CREDITS_AUTO_RENEWAL_LIMIT),
    packageId: this.fb.control<string | null>(null),
    multiplier: this.fb.nonNullable.control(
      DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER,
    ),
  });

  readonly packages = signal<CreditsAutoRenewalPackageOption[]>([]);
  readonly busy = signal(false);

  private readonly subscription = this.store.selectSignal(selectSubscription);
  private readonly formValue = toSignal(
    this.form.valueChanges.pipe(startWith(this.form.getRawValue())),
    { initialValue: this.form.getRawValue() },
  );

  readonly enabled = computed(
    () => !!this.formValue()?.creditsAutoRenewalEnabled,
  );

  readonly limited = computed(() => {
    const limit = this.formValue()?.limit;
    return (
      limit === CreditsAutoRenewalLimit.MONTH ||
      limit === CreditsAutoRenewalLimit.YEAR
    );
  });

  readonly totalKey = computed(() =>
    this.formValue()?.limit === CreditsAutoRenewalLimit.YEAR
      ? `${this.translationBase}.total-year`
      : `${this.translationBase}.total-month`,
  );

  readonly selectedPackage = computed(
    () =>
      this.packages().find((pack) => pack.id === this.formValue()?.packageId) ??
      null,
  );

  readonly multiplier = computed(
    () =>
      this.formValue()?.multiplier ?? DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER,
  );

  readonly canSaveEnabled = computed(() => {
    if (!this.canEnable() || !this.enabled()) {
      return false;
    }
    const packageId = this.formValue()?.packageId;
    const limit = this.formValue()?.limit;
    const maxPackages = this.formValue()?.multiplier;
    const needsMaxPackages = limit !== CreditsAutoRenewalLimit.UNLIMITED;
    return (
      !!packageId &&
      (!needsMaxPackages ||
        (Number.isFinite(maxPackages) &&
          (maxPackages ?? 0) >= MIN_CREDITS_AUTO_RENEWAL_PACKAGES))
    );
  });

  /**
   * Keeps the form in sync with the subscription summary and credits catalog.
   */
  private readonly hydrateEffect = effect(() => {
    this.subscription()?.subscriptionSummary?.creditsAutoRenewalEnabledAt;
    this.subscription()?.subscriptionSummary?.creditsAutoRenewalPriceId;
    this.subscription()?.subscriptionSummary?.creditsAutoRenewalMaxPackages;
    this.subscription()?.subscriptionSummary?.creditsAutoRenewalLimit;
    this.packages();
    this.formSaver();
    this.startEnabled();
    if (this.busy()) {
      return;
    }
    if (untracked(() => this.formSaver()?.hasChanges() ?? this.form.dirty)) {
      return;
    }
    untracked(() => this.hydrateFromSummary());
  });

  ngOnInit(): void {
    this.loadPackages();
  }

  /**
   * Updates local auto-renewal enabled state without persisting.
   * @param {boolean} enabled New toggle value from the UI
   * @returns {void}
   */
  onToggle(enabled: boolean): void {
    if (enabled && !this.canEnable()) {
      return;
    }
    if (this.form.controls.creditsAutoRenewalEnabled.value === enabled) {
      if (enabled) {
        this.applyDefaults();
      }
      return;
    }
    this.form.controls.creditsAutoRenewalEnabled.setValue(enabled);
    this.form.markAsDirty();
    if (enabled) {
      this.applyDefaults();
    }
  }

  /**
   * Updates the max package count from the number stepper.
   * @param {number} value Selected package count
   * @returns {void}
   */
  onMultiplierChange(value: number): void {
    if (this.busy()) {
      return;
    }
    const next = Math.max(
      MIN_CREDITS_AUTO_RENEWAL_PACKAGES,
      Math.trunc(
        Number.isFinite(value)
          ? value
          : DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER,
      ),
    );
    this.form.controls.multiplier.setValue(next);
    this.form.markAsDirty();
  }

  /**
   * Persists the current auto-renewal form via the FormSaver bar.
   * @returns {void}
   */
  onFormSaverSave(): void {
    this.save().subscribe();
  }

  /**
   * Persists the current auto-renewal form.
   * @returns {Observable<boolean>} True when the settings were saved
   */
  save(): Observable<boolean> {
    if (this.busy()) {
      return of(false);
    }

    const current = this.form.getRawValue();
    if (current.creditsAutoRenewalEnabled) {
      this.applyDefaults();
      if (!this.canSaveEnabled()) {
        this.formSaver()?.shake();
        return of(false);
      }
    }

    this.setInputsDisabled(true);
    this.busy.set(true);

    const payload = current.creditsAutoRenewalEnabled
      ? {
          enabled: true as const,
          priceId: this.form.controls.packageId.value,
          limit: this.form.controls.limit.value,
          maxPackages:
            this.form.controls.limit.value === CreditsAutoRenewalLimit.UNLIMITED
              ? null
              : this.form.controls.multiplier.value,
        }
      : { enabled: false as const };

    return this.stripeAdapter.setCreditsAutoRenewal(payload).pipe(
      tap((summary) => this.onSaveSuccess(summary)),
      map(() => true),
      catchError(() => {
        this.onSaveError();
        return of(false);
      }),
    );
  }

  /**
   * Applies a successful save response and shows a success toast.
   * @param {SubscriptionSummary} summary Updated subscription summary
   * @returns {void}
   */
  private onSaveSuccess(summary: SubscriptionSummary): void {
    this.store.dispatch(
      SubscriptionActions.patchSubscriptionSummary({
        subscriptionSummary: summary,
      }),
    );
    this.setInputsDisabled(false);
    this.hydrateFromSummary();
    this.busy.set(false);
    this.toastsService.success(
      this.showFormSaver()
        ? 'company-tool.views.settings.company.payment.toasts.payment-settings-saved'
        : 'company-tool.views.settings.company.payment.toasts.credits-auto-renewal-saved',
    );
  }

  /**
   * Restores the form from the subscription after a failed save.
   * @returns {void}
   */
  private onSaveError(): void {
    this.setInputsDisabled(false);
    this.hydrateFromSummary();
    this.busy.set(false);
    this.toastsService.alert(
      this.showFormSaver()
        ? 'company-tool.views.settings.company.payment.toasts.payment-settings-error'
        : 'company-tool.views.settings.company.payment.toasts.credits-auto-renewal-error',
    );
  }

  /**
   * Loads self-booking credits packages from Stripe.
   * @returns {void}
   */
  private loadPackages(): void {
    this.stripeAdapter.getCreditsPackages().subscribe({
      next: (prices) => {
        const options = prices
          .map((price) => this.toPackageOption(price))
          .filter(
            (pack): pack is CreditsAutoRenewalPackageOption => pack != null,
          )
          .sort((a, b) => a.unitAmountCents - b.unitAmountCents);
        this.packages.set(options);
      },
      error: () => this.packages.set([]),
    });
  }

  /**
   * Maps a Stripe catalog price to a compact auto-renewal package option.
   * @param {unknown} price Stripe price payload
   * @returns {CreditsAutoRenewalPackageOption | null}
   */
  private toPackageOption(
    price: unknown,
  ): CreditsAutoRenewalPackageOption | null {
    const record = price as {
      id?: string;
      unit_amount?: number | null;
      currency?: string;
      metadata?: Record<string, string>;
      product?:
        | string
        | {
            name?: string;
            metadata?: Record<string, string>;
          };
    };
    if (!record.id) {
      return null;
    }
    const productMeta =
      typeof record.product === 'object' && record.product
        ? record.product.metadata
        : undefined;
    const credits =
      +(
        productMeta?.['credits_amount'] ??
        record.metadata?.['credits_amount'] ??
        0
      ) || 0;
    return {
      id: record.id,
      credits,
      unitAmountCents: record.unit_amount ?? 0,
      currency: record.currency || 'eur',
    };
  }

  /**
   * Loads credits auto-renewal UI state from the current subscription summary.
   * @returns {void}
   */
  private hydrateFromSummary(): void {
    const summary = this.subscription()?.subscriptionSummary;
    const summaryEnabled = !!summary?.creditsAutoRenewalEnabledAt;
    const enabled = this.startEnabled() ? true : summaryEnabled;
    let packageId = summary?.creditsAutoRenewalPriceId ?? null;
    let multiplier =
      summary?.creditsAutoRenewalMaxPackages != null &&
      summary.creditsAutoRenewalMaxPackages >= MIN_CREDITS_AUTO_RENEWAL_PACKAGES
        ? summary.creditsAutoRenewalMaxPackages
        : DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER;
    let limit =
      summary?.creditsAutoRenewalLimit ??
      (summary?.creditsAutoRenewalMaxPackages != null
        ? CreditsAutoRenewalLimit.MONTH
        : DEFAULT_CREDITS_AUTO_RENEWAL_LIMIT);

    const packages = this.packages();
    if (
      packages.length === 0 ||
      (packageId && !packages.some((pack) => pack.id === packageId))
    ) {
      packageId = null;
    }

    if (
      !Number.isFinite(multiplier) ||
      multiplier < MIN_CREDITS_AUTO_RENEWAL_PACKAGES
    ) {
      multiplier = DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER;
    }
    if (!Object.values(CreditsAutoRenewalLimit).includes(limit)) {
      limit = DEFAULT_CREDITS_AUTO_RENEWAL_LIMIT;
    }

    if (enabled || !packageId) {
      if (packages.length > 0) {
        const selectedIsValid =
          !!packageId && packages.some((pack) => pack.id === packageId);
        if (!selectedIsValid) {
          packageId =
            packages.find(
              (pack) => pack.credits === DEFAULT_CREDITS_AUTO_RENEWAL_CREDITS,
            )?.id ?? packages[0].id;
        }
      }
    }

    const nextValue = {
      creditsAutoRenewalEnabled: enabled,
      limit,
      packageId,
      multiplier,
    };
    const currentValue = this.form.getRawValue();
    if (!isEqual(currentValue, nextValue)) {
      this.form.patchValue(nextValue, { emitEvent: true });
    }
    this.form.markAsPristine();
    this.formSaver()?.updateOriginalValue();
  }

  /**
   * Disables or enables package/multiplier controls while a save is in flight.
   * @param {boolean} disabled Whether the inputs should be disabled
   * @returns {void}
   */
  private setInputsDisabled(disabled: boolean): void {
    const options = { emitEvent: false };
    if (disabled) {
      this.form.controls.limit.disable(options);
      this.form.controls.packageId.disable(options);
      this.form.controls.multiplier.disable(options);
      return;
    }
    this.form.controls.limit.enable(options);
    this.form.controls.packageId.enable(options);
    this.form.controls.multiplier.enable(options);
  }

  /**
   * Ensures limit, multiplier and package selection have valid defaults.
   * @returns {void}
   */
  private applyDefaults(): void {
    const limit = this.form.controls.limit.value;
    if (!Object.values(CreditsAutoRenewalLimit).includes(limit)) {
      this.form.controls.limit.setValue(DEFAULT_CREDITS_AUTO_RENEWAL_LIMIT);
    }

    const multiplier = this.form.controls.multiplier.value;
    if (
      !Number.isFinite(multiplier) ||
      multiplier < MIN_CREDITS_AUTO_RENEWAL_PACKAGES
    ) {
      this.form.controls.multiplier.setValue(
        DEFAULT_CREDITS_AUTO_RENEWAL_MULTIPLIER,
      );
    }

    const packages = this.packages();
    if (packages.length === 0) {
      return;
    }

    const selectedId = this.form.controls.packageId.value;
    if (selectedId && packages.some((pack) => pack.id === selectedId)) {
      return;
    }

    const defaultPackage =
      packages.find(
        (pack) => pack.credits === DEFAULT_CREDITS_AUTO_RENEWAL_CREDITS,
      ) ?? packages[0];
    this.form.controls.packageId.setValue(defaultPackage.id);
  }
}
